import { deriveExperienceInsightsFromValues } from '@/core/live-insights'
import type { PipData } from '@/services/pip-service'
import type { AppLocale } from '@/i18n/messages'
import { normalizePipSkin, normalizePipStreamOptions, type PipSkin, type PipStreamOptions } from './pip-skins'
import type { StreamHudData } from './stream-hud'
import { compact, duration, estimate, liveRate, percent, signedCompact, statusText } from './widget-format'

/** Shared display values for everyday and stream skins in the PiP window. */
export interface WidgetSnapshot {
  version: 1
  skin: PipSkin
  options: PipStreamOptions
  locale: AppLocale
  theme: 'light' | 'dark'
  data: StreamHudData
  mesosNetTone: -1 | 0 | 1
  mesosBalanceTone: -1 | 0 | 1
  unresolvedExp: boolean
}

export function buildWidgetSnapshot(input: PipData, locale: AppLocale, theme: 'light' | 'dark'): WidgetSnapshot {
  const { session, state, liveRates, isStarting, isEnding } = input
  const en = locale === 'en'
  const ratio = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0
  const tone = (value: number | null | undefined): -1 | 0 | 1 => !value || !Number.isFinite(value) ? 0 : value > 0 ? 1 : -1
  const unresolvedExp = liveRates?.expPerHour.calculationStatus === 'unresolved-level'
  const rate = liveRates && liveRates.expPerHour.status !== 'unavailable' ? liveRates.expPerHour.value : null
  const experience = deriveExperienceInsightsFromValues(state?.exp.requiredExp, state?.exp.absolute, rate)
  const status = isEnding ? 'ending' : isStarting ? 'starting' : session?.status ?? 'idle'
  return {
    version: 1, skin: normalizePipSkin(input.skin), options: normalizePipStreamOptions(input.streamOptions), locale, theme,
    mesosNetTone: tone(session?.aggregate.mesosDelta), mesosBalanceTone: tone(session?.aggregate.mesosBalanceChange), unresolvedExp,
    data: {
      sessionId: session?.id ?? null, status,
      statusText: isEnding ? (en ? 'Finishing' : '正在结束') : isStarting ? (en ? 'Confirming' : '确认中') : statusText(session?.status, locale),
      elapsed: duration(session?.activeDurationMs ?? 0), level: state?.level == null ? '--' : String(state.level),
      expRate: unresolvedExp ? (en ? 'Unknown' : '待确认') : liveRate(liveRates?.expPerHour, locale),
      expRateValue: liveRates?.expPerHour.status === 'ready' && !unresolvedExp ? liveRates.expPerHour.value : null,
      expPercent: percent(state?.exp.ratio, 2), expRatio: ratio(state?.exp.ratio), eta: estimate(experience.etaMs, locale),
      expGained: signedCompact(session?.aggregate.expGained, locale),
      expGainedLabel: (session?.sessionAccountingVersion ?? 9) >= 9 ? (en ? 'Net EXP' : '净') : (en ? 'Gained' : '本次'),
      fullDuration: estimate(experience.fullLevelDurationMs, locale),
      mesosRate: liveRate(liveRates?.mesosPerHour, locale), mesosNet: signedCompact(session?.aggregate.mesosDelta, locale),
      mesosStart: compact(session?.aggregate.mesosStartBalance, locale), mesosBalanceChange: signedCompact(session?.aggregate.mesosBalanceChange, locale),
      hpPercent: percent(state?.hp.ratio), hpRatio: ratio(state?.hp.ratio), hpRate: liveRate(liveRates?.hpConsumedPerHour, locale),
      mpPercent: percent(state?.mp.ratio), mpRatio: ratio(state?.mp.ratio), mpRate: liveRate(liveRates?.mpConsumedPerHour, locale),
      map: input.enabledMetrics?.mapName === false ? '' : state?.mapName
        ?? (input.mapRecognitionState === 'confirming' ? (en ? 'Confirming' : '待确认') : (en ? 'Recognizing' : '识别中')),
      enabled: { ...(input.enabledMetrics ?? { mesos: true, hp: true, mp: true, mapName: true }) },
      ...(input.levelUpId ? { levelUpId: input.levelUpId } : {}),
    },
  }
}
